<template>
    <template v-for="(block, index) in demographicBlocks"
              :key="`block-${index}`">
        <div class="box-header">
            <h3>
                {{ block.title }}
                <span class="pull-right-indented">
                    <span v-if="showModified && block.modified"
                          class="modified-pill">
                        Modified
                    </span>
                    <button v-if="editable"
                            type="button"
                            class="btn portal-btn outline icon-button"
                            @click="editClicked(index, block.modified)">
                        <i class="icon icon-pencil" />
                    </button>
                </span>
            </h3>
        </div>
        <div class="box-body">
            <view-edit v-model="block.lines"
                       :comparison="history[index]"
                       :layout="block.layout"
                       :edit-mode-on="editModeOn[index]"
                       :sending="sending"
                       @cancel-pressed="cancelPressed(index)"
                       @update:model-value="updatePressed(index)" />
        </div>
    </template>
</template>

<script setup>
    import { defineProps, defineEmits, onMounted, ref, computed, nextTick } from 'vue';
    import axios from 'axios';
    import { axiosConfig, axiosError } from '../../js/AjaxUtilities.js'
    import { canEdit } from '../../js/CalcGoalPlanEditState.js';
    import ViewEdit from '../viewEdit/ViewEdit.vue';

    const props = defineProps({
        modelValue: {
            type: Array,
            default: null
        },
        goalPlanId: {
            type: Number,
            default: 0
        },
        patientId: {
            type: Number,
            default: 0
        },
        editState: {
            type: Number,
            default: 0
        },
        urlHead: {
            type: String,
            default: null
        }
    });

    const emit = defineEmits(['update:modelValue']);

    const editable = computed(() => canEdit(props.editState, 2));
    const showModified = computed(() => props.editState == 1);
    const demographicBlocks = computed(() => generateDemographicBlocks(props.modelValue));

    const editModeOn = ref([]);
    const sending = ref(false);
    const history = ref([]);

    // eslint-disable-next-line
    const goalPlanDemographicItemTypes = ecaptisEnums.goalPlanDemographicItemTypes;

    function areModified(demographicItems) {
        if (demographicItems) {
            return demographicItems.some(d => d.modified);
        } else {
            return false;
        }
    }

    function generateDemographicBlocks(demographicItems) {
        let blocks = [];

        let title = "";
        let lines = [];
        let items = [];


        for (let i = 0; i < demographicItems.length; ++i) {
            let item = demographicItems[i];
            item.item_index = i;

            if (item.itemType == 3 /* title */) {
                if (items.length > 0) {
                    let block = {
                        title: title,
                        lines: lines,
                        items: items,
                        layout: calcLayout(items, title, blocks.length),
                        modified: areModified(items)
                    };
                    blocks.push(block);
                    lines = [];
                    items = [];
                }
                title = item.name;
            } else {
                items.push(item);
                
                let attr = attrName(i);
                if (item.itemType == goalPlanDemographicItemTypes.Date) {
                    lines[attr] = item.date;
                } else {
                    lines[attr] = item.text;
                }
            }
        }

        if (items.length > 0) {
            let block = {
                title: title,
                lines: lines,
                items: items,
                layout: calcLayout(items, title, blocks.length),
                modified: areModified(items)
            };
            blocks.push(block);
        }

        return blocks;
    }

    function generateDemographicItems(demographicBlock) {
        let items = [];
        for (let j = 0; j < demographicBlock.items.length; ++j) { 
            let blockItem = demographicBlock.items[j];
            let index = blockItem.item_index;

            let item = { ...props.modelValue[index] };

            let attr = attrName(index);
            let line = demographicBlock.lines[attr];

            if (item.itemType == goalPlanDemographicItemTypes.Date) {
                item.date = line;
            } else {
                item.text = line;
            }
            items.push(item);
        }
        return items;
    }

    function calcLayout(demographicItems, title, number) {
        let layout = {
            id: `demographic-items-${number}`,
            editTitle: title,
            editSize: "dialog-adaptive",
        };
        let rows = [];

        if (demographicItems) {
            for (let i = 0; i < demographicItems.length; ++i) {
                let item = demographicItems[i];
                let viewClass = (item.itemType != goalPlanDemographicItemTypes.Title) ? "heading" : "";
                let column = {
                    title: item.name,
                    attributeName: attrName(item.item_index),
                    viewClass: viewClass
                };

                if (item.itemType == goalPlanDemographicItemTypes.Date) {
                    column.editType = "date";
                } else if (item.itemType == goalPlanDemographicItemTypes.TextBlock) {
                    column.editType = "textarea";
                } else if (item.itemType == goalPlanDemographicItemTypes.Title) { // don't put an edit in for titles
                    column.editType = "title";
                } else {
                    column.editType = "text";
                }

                let row = {
                    columns: [column]
                }

                rows.push(row);
            }
        }
        layout.rows = rows;

        return layout;
    }

    function editClicked(index, modified) {
        if (!showModified.value || !modified) {
            history.value[index] = null;
        } else {
            loadHistory(index);
        }
        editModeOn.value[index] = true;

    }

    function cancelPressed(index) {
        editModeOn.value[index] = false;
    }

    function updatePressed(index) {
        let block = demographicBlocks.value[index]
        let demographicItems = generateDemographicItems(block);
        let updateDemographicItems = {
            demographicItems: demographicItems,
            goalPlanId : props.goalPlanId,
            patientId : props.patientId
        };

        axios
            .post(`/${props.urlHead}/UpdateDemographicItems`, updateDemographicItems, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    let readDemographicItems = response.data.demographicItems;
                    emit("update:modelValue", readDemographicItems);
                } else {
                    alert(`Failed to update demographic items: ${response.data.message}`);
                }
                sending.value = false;
            })
            .catch(function (error) {
                axiosError("Update demographics", axios, error);
            })
            .finally(function () {
                editModeOn.value[index] = false;
            });
    }

    function loadHistory(index) {
        if (!history.value[index]) {
            let data = new Object();
            data.patientId = props.patientId;
            data.id = props.goalPlanId;

            axios
                .post(`/GoalPlan/GetPreviousDemographicItems`, data, axiosConfig())
                .then(function (response) {
                    if (response.data.success) {
                        let historyBlocks = generateDemographicBlocks(response.data.demographicItems);
                        if (index < historyBlocks.length) {
                            history.value[index] = historyBlocks[index].lines;
                        } else {
                            history.value[index] = null;
                        }
                        // We'll wait until the history has come through
                        nextTick(() => editModeOn.value[index] = true);
                    } else {
                        alert(`Failed to load goal history: ${response.data.message}`);
                    }
                })
                .catch(function (error) {
                    axiosError("Get previous demographics", axios, error);
                })
        }
    }

    function attrName(index) {
        return `data_${index}`;
    }


</script>